import React, { useState, useEffect, useCallback } from 'react';
import {
    Alert,
    Container,
    Typography,
    CircularProgress,
    Button,
    Chip,
    Box
} from '@mui/material';
import { get } from './hooks/apiFetch';
import { useAuth } from './Auth/AuthContext';
import '../ai/Shared.css';
import DOMPurify from 'dompurify';

export const Notification = () => {
    const { user } = useAuth();

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchProcessingNotification = useCallback(async () => {
        if (!user?.email) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const url = `/api/Notification/GetNotifications?login=${encodeURIComponent(user.email)}`;

            const data = await get(url);

            const mappedNotifications = data.map(item => ({
                id: item.id,
                creationDate: item.creationDate
                    ? new Date(item.creationDate)
                    : new Date(),
                message: item.message,
                notificationType: item.notificationType,
                link: item.link || null,
                linkText: item.linkText || 'Перейти',
                hasUnreadChanges: item.isViewed === false
            }));

            setNotifications(mappedNotifications);
        }
        catch (error) {
            let errorMessage = '';

            if (
                error.name === 'TypeError' ||
                error.message === 'Failed to fetch'
            ) {
                errorMessage =
                    'Нет соединения с сервером. Проверьте интернет.';
            } else {
                errorMessage =
                    error.message ||
                    'Ошибка загрузки уведомлений';
            }

            setError(errorMessage);
            console.error(
                'Ошибка при загрузке уведомлений:',
                error
            );
        }
        finally {
            setLoading(false);
        }
    }, [user?.email]);

    useEffect(() => {
        fetchProcessingNotification();
    }, [fetchProcessingNotification]);

    const containsLink = (text) => {
        return /https?:\/\/[^\s]+/.test(text);
    };

    const handleNotificationHover = async (itemId) => {
        const item = notifications.find(n => n.id === itemId);

        if (!item || !item.hasUnreadChanges) {
            return;
        }

        setNotifications(prev =>
            prev.map(n =>
                n.id === itemId
                    ? { ...n, hasUnreadChanges: false }
                    : n
            )
        );

        try {
            await get(
                `/api/Notification/ChangeViewStatus?guid=${encodeURIComponent(itemId)}`
            );
        }
        catch (error) {
            console.warn(
                'Не удалось обновить статус уведомления:',
                error
            );
        }
    };

    const renderMessage = (message) => {
        if (!message) {
            return null;
        }

        const hasHtml = /<[a-z][\s\S]*>/i.test(message);

        if (!hasHtml) {
            return renderMessageWithLinks(message);
        }

        const cleanHtml = DOMPurify.sanitize(message, {
            ALLOWED_TAGS: ['a', 'br', 'b', 'strong'],
            ALLOWED_ATTR: ['href', 'target', 'rel']
        });

        return (
            <span
                className="notification-message"
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
            />
        );
    };

    const renderMessageWithLinks = (message) => {
        if (!containsLink(message)) {
            return message;
        }

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = message.split(urlRegex);

        return parts.map((part, index) => {
            if (part.match(urlRegex)) {
                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="notification-link"
                    >
                        {part}
                    </a>
                );
            }

            return part;
        });
    };

    const hasUnreadNotifications = notifications.some(
        item => item.hasUnreadChanges
    );

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Уведомления
                </Typography>

                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ my: 8 }}
                >
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6">
                        Загрузка уведомлений...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={fetchProcessingNotification}
                        >
                            Попробовать снова
                        </Button>
                    }
                >
                    <Typography variant="subtitle1">
                        Ошибка при загрузке данных:
                    </Typography>

                    <Typography variant="body2">
                        {error}
                    </Typography>
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ mb: 3 }}
            >
                Уведомления
            </Typography>

            {notifications.length === 0 ? (
                <Alert severity="info">
                    Нет уведомлений
                </Alert>
            ) : (
                <section className="table-responsive">
                    <table className="table table-striped user-results-table">
                        <thead>
                            <tr>
                                {hasUnreadNotifications && <th />}
                                <th>Дата</th>
                                <th>Сообщение</th>
                            </tr>
                        </thead>

                        <tbody>
                            {notifications.map(item => (
                                <tr
                                    key={item.id}
                                    onMouseEnter={() =>
                                        handleNotificationHover(item.id)
                                    }
                                >
                                    {hasUnreadNotifications && (
                                        <td>
                                            {item.hasUnreadChanges && (
                                                <Chip
                                                    label="Новое"
                                                    size="small"
                                                    color="primary"
                                                />
                                            )}
                                        </td>
                                    )}

                                    <td>
                                        {item.creationDate.toLocaleString()}
                                    </td>

                                    <td>
                                        {renderMessage(item.message)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}
        </Container>
    );
};